/**
 * GlobalShortcuts Provider
 *
 * Renders global keyboard shortcut handlers and overlays.
 * Mounted once at the app level.
 *
 * Handles:
 *   - Cmd+K: Open command palette
 *   - Cmd+/: Toggle shortcuts overlay
 *   - Cmd+\: Toggle session panel (handled by existing hook)
 */

import * as React from "react";
import { useNavigate } from "react-router-dom";

import { ShortcutsOverlay } from "@/shared/components/ShortcutsOverlay";
import { CommandPalette } from "@/shared/components/CommandPalette";
import { ChordIndicator } from "@/shared/components/ChordIndicator";
import { useShortcut } from "@/shared/hooks/useShortcut";
import { useUIStore } from "@/shared/stores/ui-store";

export function GlobalShortcuts({ children }: { children: React.ReactNode }) {
  const navigate = useNavigate();
  const setCommandPaletteOpen = useUIStore((s) => s.setCommandPaletteOpen);
  const toggleSessionPanel = useUIStore((s) => s.toggleSessionPanel);

  // Cmd+K: Open command palette
  useShortcut("command-palette", () => {
    setCommandPaletteOpen(true);
  });

  // Cmd+\: Toggle session panel
  useShortcut("toggle-session-panel", () => {
    toggleSessionPanel();
  });

  // Register all go-to-* chord shortcuts
  useShortcut("go-to-events", () => navigate("/events"));
  useShortcut("go-to-queue", () => navigate("/queue"));
  useShortcut("go-to-health", () => navigate("/health"));
  useShortcut("go-to-customers", () => navigate("/customers"));
  useShortcut("go-to-agents", () => navigate("/agents"));
  useShortcut("go-to-sessions", () => navigate("/sessions"));
  useShortcut("go-to-automations", () => navigate("/automations"));
  useShortcut("go-to-inbox", () => navigate("/inbox"));
  useShortcut("go-to-catalog", () => navigate("/catalog"));
  useShortcut("go-to-purchases", () => navigate("/purchases"));
  useShortcut("go-to-email", () => navigate("/email"));
  useShortcut("go-to-agent-cost", () => navigate("/agent-cost"));
  useShortcut("go-to-eng-dashboard", () => navigate("/engineering"));
  useShortcut("go-to-deferred", () => navigate("/deferred"));
  useShortcut("go-to-artifacts", () => navigate("/artifacts"));
  useShortcut("go-to-integrations", () => navigate("/integrations"));
  useShortcut("go-to-email-health", () => navigate("/email-health"));

  return (
    <>
      {children}
      {/* Shortcuts overlay - mounts its own Cmd+/ handler */}
      <ShortcutsOverlay />
      {/* Command palette */}
      <CommandPalette />
      {/* Chord indicator */}
      <ChordIndicator />
    </>
  );
}

export default GlobalShortcuts;
